import { useAppContext } from '../../../context/AppContext';
import { postData } from '../../../services/postData';
import { markPendingCompanyCreated } from '../../../services';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
export const SignUpPage = () => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        job_title: '',
        phone: '',
        company_name: '',
        company_domain: '',
        company_type: '',
        user_type: 'Standard',
        is_admin: true,
        is_super_admin: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [pendingEmailVerification, setPendingEmailVerification] = useState(false);
    const [submittedEmail, setSubmittedEmail] = useState('');
    const [verificationMessage, setVerificationMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { base_url, setToken } = useAppContext();

    const companyTypes = [
        'End User / Property Owner',
        'Installer / Solar Pro',
        'Electrical / General Contractor',
        'Distributor',
        'EPC / Developer'
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isSubmitting) return; // Prevent multiple submissions
        
        setIsSubmitting(true);
        setError('');

        // Validate that a company type is selected
        if (!formData.company_type || formData.company_type === '') {
            setError('Please select a company type.');
            setIsSubmitting(false);
            return;
        }

        const companyDomainRequired =
            formData.company_type === 'Distributor' || formData.company_type === 'Installer';
        if (companyDomainRequired && !formData.company_domain.trim()) {
            setError('Company domain is required for Distributor and Installer company types.');
            setIsSubmitting(false);
            return;
        }

        // If company_name is not provided, use first_name + last_name
        const submitData = {
            ...formData,
            company_name: formData.company_name.trim() || `${formData.first_name} ${formData.last_name}`.trim(),
            company_domain: formData.company_domain.trim()
        };

        try {
            console.log('Signing up with data:', submitData);
            const response = await postData(null, `${base_url}/apis/admin/signup`, submitData);
            const isPendingEmailVerification =
                response?.httpStatus === 290 ||
                response?.status === 290 ||
                Number(response?.status) === 290;

            if (isPendingEmailVerification) {
                setPendingEmailVerification(true);
                setSubmittedEmail(submitData.email);
                setVerificationMessage(
                    response?.msg || 'Your account was created. Please check your email to verify your account before signing in.'
                );
                setError('');
                console.log('Sign up pending email verification for existing company');
            } else if (response.token) {
                setToken(response.token, true);
                sessionStorage.setItem('newCompanySignup', '1');
                markPendingCompanyCreated('email');
                console.log('Sign up successful!');
            } else {
                setError(response?.msg || 'Sign up failed. Please try again.');
                if (window.showAlert) {
                    window.showAlert(response?.status || 400, response?.msg || 'Sign up failed. Please try again.');
                }
            }
        } catch (err) {
            setError('Sign up failed. Please try again.');
            console.error('Sign up error:', err);
            
            // Show error alert based on error type
            if (window.showAlert) {
                if (err.status >= 400 && err.status < 500) {
                    window.showAlert(err.status, 'Invalid signup data. Please check your input.');
                } else {
                    window.showAlert(500, 'Failed to create account. Please try again.');
                }
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const companyDomainRequired =
        formData.company_type === 'Distributor' || formData.company_type === 'Installer';

    if (pendingEmailVerification) {
        return (
            <div className="modal-content auth-page__card--wide">
                <h2 className="modal-title modal-title--centered">Check Your Email</h2>
                <div className="banner banner--emphasis">
                    <p>{verificationMessage}</p>
                    <p>
                        We sent a verification link to <strong>{submittedEmail}</strong>. Click the link in that email to finish setting up your account and join your company.
                    </p>
                </div>
                <p className="auth-page__muted-note">
                    Didn't receive it? Check your spam folder or try signing up again with the same email.
                </p>
                <p className="auth-page__center">
                    <Link to="/login" className="text-link">
                        Return to sign in
                    </Link>
                </p>
            </div>
        );
    }

    return (
        <div className="modal-content auth-page__card--signup">
                <h2 className="modal-title modal-title--flush">Create Account</h2>
                
                {error && (
                    <div className="form-error-banner">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* User Information Section */}
                    <div className="form-section">
                        <h3 className="form-section-heading">
                            Personal Information
                        </h3>
                        
                        <div className="form-row">
                            <div className="form-group form-group--compact">
                                <label htmlFor="first_name" className="form-label">
                                    First Name *
                                </label>
                                <input
                                    type="text"
                                    id="first_name"
                                    name="first_name"
                                    className="form-input"
                                    value={formData.first_name}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Enter your first name"
                                />
                            </div>

                            <div className="form-group form-group--compact">
                                <label htmlFor="last_name" className="form-label">
                                    Last Name *
                                </label>
                                <input
                                    type="text"
                                    id="last_name"
                                    name="last_name"
                                    className="form-input"
                                    value={formData.last_name}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Enter your last name"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group--compact">
                                <label htmlFor="email" className="form-label">
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    className="form-input"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Enter your email"
                                />
                            </div>

                            <div className="form-group form-group--compact">
                                <label htmlFor="password" className="form-label">
                                    Password *
                                </label>
                                <div className="password-field">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        name="password"
                                        className="form-input"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Create a password"
                                        minLength="8"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="password-toggle"
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group--compact">
                                <label htmlFor="job_title" className="form-label">
                                    Job Title
                                </label>
                                <input
                                    type="text"
                                    id="job_title"
                                    name="job_title"
                                    className="form-input"
                                    value={formData.job_title}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Project Manager"
                                />
                            </div>

                            <div className="form-group form-group--compact">
                                <label htmlFor="phone" className="form-label">
                                    Phone
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    className="form-input"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="(555) 123-4567"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Company Information Section */}
                    <div className="form-section">
                        <h3 className="form-section-heading">
                            Company Information
                        </h3>
                        
                        <div className="form-row">
                            <div className="form-group form-group--compact">
                                <label htmlFor="company_name" className="form-label">
                                    Company Name (leave blank if you are an individual)
                                </label>
                                <input
                                    type="text"
                                    id="company_name"
                                    name="company_name"
                                    className="form-input"
                                    value={formData.company_name}
                                    onChange={handleInputChange}
                                    placeholder="Enter your company name"
                                />
                            </div>

                            <div className="form-group form-group--compact">
                                <label htmlFor="company_type" className="form-label">
                                    Company Type *
                                </label>
                                <select
                                    id="company_type"
                                    name="company_type"
                                    className="form-select"
                                    value={formData.company_type}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select company type</option>
                                    {companyTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group form-group--compact">
                            <label htmlFor="company_domain" className="form-label">
                                Company domain{companyDomainRequired ? ' *' : ''}
                            </label>
                            <input
                                type="text"
                                id="company_domain"
                                name="company_domain"
                                className="form-input"
                                value={formData.company_domain}
                                onChange={handleInputChange}
                                required={companyDomainRequired}
                                placeholder="e.g., yourcompany.com"
                            />
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            type="submit"
                            className="btn btn-primary btn--block"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin /> Creating Account...
                                </>
                            ) : (
                                'Create Account'
                            )}
                        </button>
                    </div>
                </form>
                
                <div className="auth-page__footer">
                    <p className="auth-page__footer-p">
                        Already have an account?{' '}
                        <Link to="/" className="text-link">
                            Sign in here
                        </Link>
                    </p>
                    <p className="auth-page__footer-p">
                        By creating an account, you agree to our{' '}
                        <Link to="/terms" className="text-link">
                            Terms of Service
                        </Link>
                        {' '}and{' '}
                        <Link to="/privacy" className="text-link">
                            Privacy Policy
                        </Link>
                    </p>
                </div>
            </div>
    );
};
